import { createClient } from '@supabase/supabase-js';

// The Yhuu Test compatibility quiz (served at /test) lives in its own, separate
// Supabase project — a deliberate choice to keep quiz data isolated from the main
// Yhuu account database. This client is READ-ONLY from Yhuu's side: we only use it
// to pull a user's quiz history for display on their profile. Nothing here writes
// to this project. The quiz's tables use fully open RLS policies (public read/write
// via the anon key), matching how the quiz app itself connects, so no extra secret
// is needed beyond the public anon key.
const QUIZ_SUPABASE_URL = 'https://pxydpmkdxxnehslmnhbx.supabase.co';
const QUIZ_SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB4eWRwbWtkeHhuZWhzbG1uaGJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODkzOTI1NjQsImV4cCI6MjEwNDk2ODU2NH0.SonMPvwi7fkmcZqOtEJUeb6ZOr2eB1YM93MZfWiUNJ4';

export const quizSupabase = createClient(QUIZ_SUPABASE_URL, QUIZ_SUPABASE_ANON_KEY);

export interface QuizResponse {
  id: string;
  session_id: string;
  responder_name: string;
  score: number;
  total: number;
  created_at: string;
}

export interface QuizSession {
  id: string;
  creator_id: string | null;
  creator_name: string;
  partner_name: string | null;
  category: string;
  quiz_mode: 'private' | 'public';
  created_at: string;
  public_responses: QuizResponse[];
}

// Fetch every Yhuu Test quiz this user has created, along with any responses
// received, ordered newest first.
export async function getQuizHistory(yhuuUserId: string): Promise<QuizSession[]> {
  const { data, error } = await quizSupabase
    .from('public_sessions')
    .select('id, creator_id, creator_name, partner_name, category, quiz_mode, created_at, public_responses(*)')
    .eq('creator_id', yhuuUserId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('getQuizHistory error:', error);
    return [];
  }
  return (data as unknown as QuizSession[]) || [];
}

// Delete a Yhuu Test session (and any response tied to it) from the creator's
// history. The quiz project's RLS is open read/write via the anon key, same
// as the quiz app itself uses, so this works directly from here.
export async function deleteQuizSession(sessionId: string): Promise<boolean> {
  const { error: responsesError } = await quizSupabase
    .from('public_responses')
    .delete()
    .eq('session_id', sessionId);
  if (responsesError) {
    console.error('deleteQuizSession (responses) error:', responsesError);
    return false;
  }
  const { error: sessionError } = await quizSupabase
    .from('public_sessions')
    .delete()
    .eq('id', sessionId);
  if (sessionError) {
    console.error('deleteQuizSession (session) error:', sessionError);
    return false;
  }
  return true;
}
